import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import ThemeContext from '../contexts/themeContext';
import Aside from '../layout/Aside/Aside';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { authPages } from '../menu';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import { http, httpwithtoken } from '../services/http';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, store } from '../store/store';
import { runOneSignal } from '../pages/notifications/components/oneSignal';
import LoginPage from '../pages/auth/login/Login';
import { dataIngestionOnUnload } from '../services/commonService';

// 10 minutes of inactivity → show session expired popup
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

// User activity events that reset the idle timer
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'click'] as const;

const ACTIVITY_STORAGE_KEY = 'lastActivityAt';
const ACTIVITY_BROADCAST_THROTTLE_MS = 5000;

const App = () => {
	getOS();
	const dispatch = useDispatch<any>();
	let navigate = useNavigate();
	const { t } = useTranslation(['notification']);
	const RefToAvoidRepetation = useRef(0);
	const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { dir } = useSelector((state: RootState) => state.appStore);
	const location = useLocation();

	// Controls the session-expired modal
	const [showSessionModal, setShowSessionModal] = useState(false);

	// True only when user is actually logged in (token exists in store)
	const isLoggedIn = Boolean(store.getState().auth.token);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	React.useEffect(() => {
		document.body.dir = dir;
	}, [dir]);

	useEffect(() => {
		runOneSignal();
	}, []);

	/**
	 * Full Screen
	 */
	// @ts-ignore
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	//	Add paths to the array that you don't want to be "Aside".
	const withOutAsidePages = [
		authPages.login.path,
		authPages.forgetPassword.path,
		authPages.resetPassword.path,
		authPages.page404.path,
	];
	const withOutAsidePagesTwo = [
		authPages.login.path,
		authPages.forgetPassword.path,
		// authPages.resetPassword.path,
		authPages.page404.path,
	];

	useEffect(() => {
		const currentPagePath = location.pathname.substring(1);
		const locationRoute = location;
		const isResetPasswordPage = locationRoute.hash.includes('#/reset-password?token');
		const isCurrentPageInList = withOutAsidePagesTwo.includes(currentPagePath);

		if (store.getState().auth.user.user === undefined) {
			if (!isResetPasswordPage && !isCurrentPageInList) {
				navigate('/', { state: location.pathname });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [store.getState().auth.user.user]);

	useEffect(() => {
		fetch('https://api.ipify.org?format=json')
			.then((response) => response.json())
			.then((data) => dispatch.appStore.updateIpAddress(data.ip))
			.catch((error) => console.log(error));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

useEffect(() => {
	const handleBeforeUnload = () => {
		const { auth } = store.getState();
		if (!auth.token) return; // not logged in, nothing to report

		const loginTime = auth.loginTime || localStorage.getItem('login_time');
		const logoutTime = new Date();
		let sessionDurationLabel = '';

		if (loginTime) {
			const diffMs = logoutTime.getTime() - new Date(loginTime).getTime();
			const totalSeconds = Math.max(0, Math.round(diffMs / 1000));
			const hours = Math.floor(totalSeconds / 3600);
			const minutes = Math.floor((totalSeconds % 3600) / 60);
			const seconds = totalSeconds % 60;
			sessionDurationLabel = `${hours}h ${minutes}m ${seconds}s`;
		}

		dataIngestionOnUnload({
			action: 'log activity (tab closed)',
			login_time: loginTime,
			logout_time: logoutTime.toISOString(),
			session_duration: sessionDurationLabel,
		});
	};

	window.addEventListener('beforeunload', handleBeforeUnload);
	return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, []);

	// ── Logout: clears store and goes back to login ──────────────────────────
	const doLogout = useCallback(async () => {
		RefToAvoidRepetation.current = 0;
		await dispatch.auth.deleteTokenAsync();
		navigate('/', { replace: true });
	}, [dispatch, navigate]);

	// ── "Log In Again" button on modal ───────────────────────────────────────
	const handleModalClose = useCallback(async () => {
		setShowSessionModal(false);
		await doLogout();
	}, [doLogout]);

	// ── Idle timer: reset on any user activity ───────────────────────────────
	// Only starts when user is logged in. Shows modal after 10 min of silence.
		const resetIdleTimer = useCallback(() => {
		if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		if (!store.getState().auth.token) return; // not logged in, do nothing
		idleTimerRef.current = setTimeout(() => {
			setShowSessionModal(true);
		}, IDLE_TIMEOUT_MS);
	}, []);

	const lastBroadcastRef = useRef(0);
	const broadcastActivity = useCallback(() => {
		const now = Date.now();
		if (now - lastBroadcastRef.current < ACTIVITY_BROADCAST_THROTTLE_MS) return;
		lastBroadcastRef.current = now;
		try {
			localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
		} catch {
			// ignore
		}
	}, []);

	const handleActivity = useCallback(() => {
		resetIdleTimer();
		broadcastActivity();
	}, [resetIdleTimer, broadcastActivity]);

	useEffect(() => {
		if (!isLoggedIn) {
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
			return;
		}

		resetIdleTimer();

		ACTIVITY_EVENTS.forEach((event) =>
			window.addEventListener(event, handleActivity, { passive: true, capture: true }),
		);

		const onStorage = (e: StorageEvent) => {
			if (e.key === ACTIVITY_STORAGE_KEY) resetIdleTimer();
		};
		window.addEventListener('storage', onStorage);

		return () => {
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
			ACTIVITY_EVENTS.forEach((event) =>
				window.removeEventListener(event, handleActivity, { capture: true } as EventListenerOptions),
			);
			window.removeEventListener('storage', onStorage);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoggedIn]);

	// ── HTTP interceptors ────────────────────────────────────────────────────
	
		useEffect(() => {
		const handleAuthError = (error: any) => {
			if (error?.response?.status === 401 && RefToAvoidRepetation.current === 0) {
				RefToAvoidRepetation.current = 1;
				setShowSessionModal(true);
			}
			return Promise.reject(error);
		};

		const httpWithTokenInterceptorId = httpwithtoken.interceptors.response.use(
			(response) => response,
			handleAuthError,
		);
		const httpInterceptorId = http.interceptors.response.use(
			(response) => response,
			handleAuthError,
		);

		return () => {
			httpwithtoken.interceptors.response.eject(httpWithTokenInterceptorId);
			http.interceptors.response.eject(httpInterceptorId);
		};
	}, []);

	return (
		<ThemeProvider theme={theme}>
			<ToastProvider components={{ ToastContainer, Toast }}>
				<Routes>
					<Route path='/' element={<LoginPage />} />
					{withOutAsidePages.map((path) => (
						<Route key={path} path={path} />
					))}
					<Route path='*' element={<Aside />} />
				</Routes>
				<Wrapper />

				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>

				{/* Session Expired Modal — only shown after 10 min idle OR 401 error */}
				{showSessionModal && (
					<div
						style={{
							position: 'fixed',
							inset: 0,
							background: 'rgba(0,0,0,0.55)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							zIndex: 9999,
						}}>
						<div
							style={{
								background: '#fff',
								borderRadius: 12,
								padding: '2rem 2.5rem',
								maxWidth: 400,
								width: '90%',
								textAlign: 'center',
								boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
							}}>
							<div style={{ fontSize: 40, marginBottom: 8 }}>⏱</div>
							<h3 style={{ marginBottom: '0.5rem', color: '#c0392b' }}>
								{t('sessionExpiredTitle', 'Session Expired')}
							</h3>
							<p style={{ color: '#555', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
								{t(
									'sessionExpiredMessage',
									'You have been inactive for 10 minutes. Please log in again to continue.',
								)}
							</p>
							<button
								onClick={handleModalClose}
								style={{
									background: '#2563eb',
									color: '#fff',
									border: 'none',
									borderRadius: 8,
									padding: '0.65rem 2.2rem',
									fontSize: '1rem',
									cursor: 'pointer',
									fontWeight: 600,
								}}>
								{t('sessionExpiredButton', 'Log In Again')}
							</button>
						</div>
					</div>
				)}
			</ToastProvider>
		</ThemeProvider>
	);
};

export default App;